/*
 * Black Friday 2026 - VIP Early Access (nur Anzeige). Gegenstueck zu snippets/ll-bf26.liquid.
 *
 * 1. Zustand: prueft den Early-Access-Zeitraum im Browser (Date.now()), weil Shopify gerenderte Seiten cached, und
 *    setzt ihn als Tokens auf <html data-ll-bf26="...">. Elemente mit data-ll-bf26-when/-hide schaltet dann das CSS
 *    aus snippets/ll-bf26.liquid um. Zum naechsten Start/Ende wird ohne Reload umgeschaltet (setTimeout).
 *    Bei jeder Aenderung feuert "ll-bf26:change" auf document (detail.tokens).
 * 2. <ll-bf26-price>: rechnet den BF-Preis der Variante in die Shop-Waehrung des Besuchers um und blendet ihn ein.
 *    Rechnung wie in der Discount Function: Math.round(bfPreis * Shopify.currency.rate * 100) Cent, ohne
 *    Marktrundung. Gueltig nur, wenn > 0 und unter dem aktuellen Variantenpreis. Sonst bleibt alles verborgen.
 *
 * Die Theme-Preise werden nicht veraendert, nur per CSS durchgestrichen (price-list[data-ll-bf26-active]).
 * Warenkorb und Checkout rechnet die Function - hier nichts.
 */
const MAX_TIMEOUT = 2147483647;
const AUDIENCES = ["vip", "customer", "guest"];

const readJson = (element) => {
  try {
    return element ? JSON.parse(element.textContent) : null;
  } catch {
    return null;
  }
};

const config = readJson(document.getElementById("ll-bf26-config"));
const start = Date.parse(config?.start);
const end = Date.parse(config?.end);
const audience = AUDIENCES.includes(config?.audience) ? config.audience : null;
const isValid = Boolean(config && audience && Number.isFinite(start) && Number.isFinite(end) && start < end);

let tokens = [];
let timer = null;
let formatMoney = null;
const priceElements = new Set();

const getTokens = (now) => {
  if (!isValid) {
    return [];
  }
  if (now < start) {
    return ["before"];
  }
  if (now >= end) {
    return ["after"];
  }
  const result = ["active", audience];
  if (audience !== "vip" && config.teaser === true) {
    result.push("teaser");
  }
  return result;
};

const isVipActive = () => tokens.includes("active") && tokens.includes("vip");

const getRate = () => {
  const rate = Number(window.Shopify?.currency?.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
};

const formatPrice = (cents) => {
  const settings = window.themeVariables?.settings || {};
  const format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
  return formatMoney(cents, format);
};

const update = () => {
  const now = Date.now();
  const next = getTokens(now);

  if (next.join(" ") !== tokens.join(" ")) {
    tokens = next;
    if (tokens.length) {
      document.documentElement.setAttribute("data-ll-bf26", tokens.join(" "));
    } else {
      document.documentElement.removeAttribute("data-ll-bf26");
    }
    priceElements.forEach((element) => element.render());
    document.dispatchEvent(new CustomEvent("ll-bf26:change", { detail: { tokens: [...tokens] } }));
  }

  clearTimeout(timer);
  const boundary = [start, end].find((time) => time > now);
  if (isValid && boundary) {
    // +50 ms, damit beim Aufwachen sicher der neue Zustand gilt.
    timer = setTimeout(update, Math.min(boundary - now + 50, MAX_TIMEOUT));
  }
};

class BfPrice extends HTMLElement {
  connectedCallback() {
    this.variants = readJson(this.querySelector("[data-ll-bf26-variants]")) || {};
    this.tag = this.querySelector(".ll-bf26-price__tag");
    this.tagDefault = this.tag?.textContent || "";
    this.variantId = this.getAttribute("variant");
    this.formId = this.getAttribute("form");
    this.abortController = new AbortController();

    if (this.formId) {
      document.addEventListener("variant:change", (event) => {
        const variant = event.detail?.variant;
        // getAttribute statt .id: Das Produktformular enthaelt <input name="id">, dadurch liefert form.id das
        // Eingabefeld statt der Formular-ID und der Vergleich waere nie wahr.
        if (event.target?.getAttribute?.("id") === this.formId && variant?.id) {
          this.variantId = String(variant.id);
          // Das Theme hat Preis und Ersparnis fuer die neue Variante gerade neu geschrieben (seine Listener am
          // Formular laufen vor diesem hier) - gespeicherte Originale sind damit veraltet.
          this.themeOriginal = null;
          this.render();
        }
      }, { signal: this.abortController.signal });
    }

    priceElements.add(this);
    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    priceElements.delete(this);
    this.setActive(false);
  }

  /* BF-Preis einer Variante in Cent der aktuellen Waehrung, oder null. Regel identisch zur Discount Function. */
  evaluate(id, rate) {
    const entry = this.variants[id];
    if (!Array.isArray(entry)) {
      return null;
    }
    const [price, compareAtPrice, available, bfPrice] = entry;
    const bfAmount = Number(bfPrice);
    if (!Number.isFinite(bfAmount) || bfAmount <= 0 || !Number.isFinite(price)) {
      return null;
    }
    const cents = Math.round(bfAmount * rate * 100);
    if (cents >= price) {
      return null;
    }
    return { cents, price, compareAtPrice, available: available === 1 };
  }

  /* Produktseite/Karte: die gewaehlte Variante. Ohne Variante (Produktansicht): guenstigste gueltige, verfuegbare zuerst. */
  pick(rate) {
    if (this.variantId) {
      const result = this.evaluate(this.variantId, rate);
      return result ? { ...result, from: false } : null;
    }
    const results = Object.keys(this.variants).map((id) => this.evaluate(id, rate)).filter(Boolean);
    const pool = results.some((result) => result.available) ? results.filter((result) => result.available) : results;
    if (!pool.length) {
      return null;
    }
    const best = pool.reduce((a, b) => (b.cents < a.cents ? b : a));
    return { ...best, from: this.hasAttribute("price-varies") };
  }

  render() {
    const rate = getRate();
    const result = isVipActive() && formatMoney && rate ? this.pick(rate) : null;

    // Design BF26 (assets/ll-design-bf26.css): Ersparnis nur gegenueber der UVP, ohne UVP keine Ersparnis.
    const designBf26 = Boolean(this.closest("price-list.ll-design--bf26"));
    let priceText = "";
    let savingsText = "";
    let savingsAmount = "";

    if (result) {
      const strings = config.strings || {};
      const price = `${formatPrice(result.cents)}*`;
      // Classic: Referenz ist die UVP, wenn die Variante reduziert ist, sonst der aktuelle Preis.
      // Design BF26: nur die UVP - und zwar sobald sie ueber dem BF-Preis liegt (auch wenn der aktuelle Preis = UVP ist).
      const reference = designBf26
        ? (result.compareAtPrice > result.cents ? result.compareAtPrice : 0)
        : (result.compareAtPrice > result.price ? result.compareAtPrice : result.price);
      const amount = this.querySelector("[data-ll-bf26-amount]");
      const savings = this.querySelector("[data-ll-bf26-savings]");

      priceText = result.from && strings.from ? strings.from.replace("%amount%", price) : price;
      savingsAmount = !result.from && reference > result.cents && strings.savings ? formatPrice(reference - result.cents) : "";
      savingsText = savingsAmount ? strings.savings.replace("%amount%", savingsAmount) : "";

      if (amount) {
        amount.textContent = priceText;
      }
      if (savings) {
        savings.textContent = savingsText;
      }

      // Design BF26: Label mit Rabatt gegenueber der UVP ("-28% Early Access"). Kaufmaennisch gerundet wie das
      // Rabatt-Badge des Themes (on-sale-badge). Ohne UVP bleibt der Standardtext.
      if (this.tag) {
        const percent = designBf26 && !result.from && reference > result.cents
          ? Math.round(((reference - result.cents) * 100) / reference)
          : 0;
        if (this.hasAttribute("data-card")) {
          // Produktkarte: nur "-36%" neben dem Preis (Label "Black Deal" steht darueber), ohne UVP kein %-Label
          this.tag.textContent = percent > 0 ? `-${percent}%` : "";
          this.tag.hidden = !(percent > 0);
        } else {
          this.tag.textContent = percent > 0 && strings.badgePercent
            ? strings.badgePercent.replace("%percent%", percent)
            : this.tagDefault;
        }
      }
    }

    this.hidden = !result;
    this.setActive(Boolean(result));
    if (designBf26) {
      // 27.10.2026 (VALI SHOP, Variante A): Preis ohne Black Access (aktueller Shop-Preis) + Extra-Ersparnis
      const member = result && !result.from && result.price > result.cents ? { price: result.price, cents: result.cents, compareAtPrice: result.compareAtPrice } : null;
      this.applyToThemePrice(Boolean(result), priceText, savingsText, Boolean(result && !result.from && result.compareAtPrice > result.cents), savingsAmount, member);
    }
  }

  /*
   * Variante A (VALI SHOP, 27.10.2026): Unter dem Black-Preis "Preis ohne Black Access <s>Shop-Preis</s>" und
   * "<Betrag> extra gespart mit Black Access". Nur grosser Preis der Produktseite (price-list--lg), nicht im
   * Sticky-Warenkorb, nicht bei "ab"-Preisen. Referenz ist der aktuelle Preis der Variante (variant.price), also genau
   * der Preis, den Gaeste sehen. Gibt true zurueck, wenn die Zeilen sichtbar sind.
   */
  applyMember(list, member) {
    if (!list) return false;
    let el = list.querySelector(":scope > [data-ll-bf26-member]");
    const strings = config.strings || {};
    const show = Boolean(member && member.price > member.cents && list.classList.contains("price-list--lg")
      && !list.closest(".product-quick-add") && strings.memberWas && strings.memberSavings);
    /* 02.11.2026 (Dominic, "Du sparst gross"): Variante reduziert = Gesamtersparnis gross, Extra-Anteil darunter.
       Sonst (ohne UVP oder Shop-Preis = UVP) bleibt "Preis ohne Black Access", weil gross nichts anderes stuende. */
    const sparen = Boolean(show && member.compareAtPrice > member.price && strings.memberOf && strings.savingsRef);
    list.toggleAttribute("data-ll-bf26-sparen", sparen);
    if (!show) {
      el?.remove();
      return false;
    }
    if (!el) {
      el = document.createElement("div");
      el.className = "ll-bf26-member";
      el.setAttribute("data-ll-bf26-member", "");
    }
    const row = list.querySelector("compare-at-price")?.parentElement;
    if (row && row.parentElement === list) {
      if (sparen) {
        row.after(el);
      } else {
        list.insertBefore(el, row);
      }
    } else if (!el.parentElement) {
      list.appendChild(el);
    }
    const was = document.createElement("span");
    was.className = "ll-bf26-member__was";
    const strike = document.createElement("s");
    strike.textContent = formatPrice(member.price);
    was.append(document.createTextNode(`${strings.memberWas} `), strike);
    const win = document.createElement("span");
    win.className = "ll-bf26-member__win";
    win.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 18" width="12" height="14"><path fill="currentColor" d="M8 0a5 5 0 0 0-5 5v3H2a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-1V5a5 5 0 0 0-5-5Zm-3 5a3 3 0 1 1 6 0v3H5V5Z"/></svg>';
    const extra = formatPrice(member.price - member.cents);
    win.append(document.createTextNode((sparen ? strings.memberOf : strings.memberSavings).replace("%amount%", extra)));
    if (sparen) {
      el.replaceChildren(win);
    } else {
      el.replaceChildren(was, win);
    }
    return true;
  }

  /*
   * Design BF26: Der BF-Preis steht in den normalen Preisfeldern des Themes (sale-price, price-savings), damit Preis
   * mit und ohne Early Access exakt gleich aussieht. Vom BF-Block bleibt dort nur das Badge (CSS blendet seine
   * Preiszeile aus). Ohne Early Access (z. B. Zeitraum endet) wird der vorherige Inhalt wiederhergestellt.
   */
  applyToThemePrice(active, priceText, savingsText, showCompare, savingsAmount = "", member = null) {
    const list = this.closest("price-list");
    const sale = list?.querySelector("sale-price");
    const savings = list?.querySelector("price-savings");
    const compare = list?.querySelector("compare-at-price");

    if (active) {
      if (!this.themeOriginal) {
        this.themeOriginal = {
          saleHtml: sale?.innerHTML,
          saleClass: sale?.className,
          savingsHtml: savings?.innerHTML,
          savingsHidden: savings?.hidden,
          compareHidden: compare?.hidden,
        };
      }
      // UVP-Zeile zeigen, sobald die UVP ueber dem BF-Preis liegt - das Theme blendet sie aus, wenn die Variante
      // selbst nicht reduziert ist (UVP = aktueller Preis). Den Betrag hat das Theme bereits gerendert.
      if (compare && showCompare) {
        compare.hidden = false;
      }
      if (sale) {
        sale.lastChild?.replaceWith(document.createTextNode(priceText));
        sale.classList.add("text-on-sale");
      }
      if (savings) {
        // Betrag hervorgehoben wie im Liquid-Markup (<strong class="ll-savings__amount">, rot) - direkt hier, damit es
        // auch ohne assets/ll-design-bf26.js stimmt (Desktop-Sticky ist auch im Classic-Design BF26).
        const template = config.strings?.savings || "";
        const [prefix, suffix = ""] = template.split("%amount%");
        if (savingsAmount && template.includes("%amount%")) {
          const strong = document.createElement("strong");
          strong.className = "ll-savings__amount";
          strong.textContent = savingsAmount;
          savings.replaceChildren(document.createTextNode(prefix), strong, document.createTextNode(suffix));
        } else {
          savings.textContent = savingsText;
        }
        savings.hidden = !savingsText;
      }
      // Variante A: mit Shop-Preis-Zeile entfaellt die UVP-Ersparnis (sonst zwei Ersparnisse untereinander)
      const memberShown = this.applyMember(list, member);
      const sparen = Boolean(list?.hasAttribute("data-ll-bf26-sparen"));
      if (savings && memberShown && !sparen) {
        savings.hidden = true;
      }
      if (savings && sparen) {
        const ref = document.createElement("span");
        ref.className = "ll-savings__ref";
        ref.textContent = config.strings.savingsRef;
        savings.append(document.createTextNode(" "), ref);
      }
      // Nicht reduzierte Variante (Shop-Preis = UVP): die UVP-Zeile wuerde dieselbe Zahl noch einmal zeigen
      if (compare && memberShown && member.price >= member.compareAtPrice) {
        compare.hidden = true;
      }
      return;
    }

    this.applyMember(list, null);

    if (this.themeOriginal) {
      if (sale && this.themeOriginal.saleHtml != null) {
        sale.innerHTML = this.themeOriginal.saleHtml;
        sale.className = this.themeOriginal.saleClass;
      }
      if (savings && this.themeOriginal.savingsHtml != null) {
        savings.innerHTML = this.themeOriginal.savingsHtml;
        savings.hidden = this.themeOriginal.savingsHidden;
      }
      if (compare && this.themeOriginal.compareHidden != null) {
        compare.hidden = this.themeOriginal.compareHidden;
      }
      this.themeOriginal = null;
    }
  }

  setActive(active) {
    this.closest("price-list")?.toggleAttribute("data-ll-bf26-active", active);
    this.closest("product-card")?.toggleAttribute("data-ll-bf26-active", active);
    if (this.formId) {
      document.querySelectorAll(`on-sale-badge[form="${CSS.escape(this.formId)}"]`)
        .forEach((badge) => badge.classList.toggle("ll-bf26-suppressed", active));
    }
  }
}

if (isValid) {
  update();
  // Timer laufen in Hintergrund-Tabs gedrosselt; beim Zurueckkommen (auch aus dem bfcache) neu pruefen.
  document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && update());
  window.addEventListener("pageshow", update);
}

// Preise erst formatieren, wenn der Money-Helper des Themes geladen ist. Gleiche URL wie in layout/theme.liquid,
// dadurch dieselbe Modul-Instanz. Schlaegt der Import fehl, bleibt der BF-Preis verborgen (fail closed).
if (isValid && config.themeScript) {
  import(config.themeScript)
    .then((theme) => {
      if (typeof theme.formatMoney === "function") {
        formatMoney = theme.formatMoney;
        priceElements.forEach((element) => element.render());
      }
    })
    .catch(() => {});
}

if (!window.customElements.get("ll-bf26-price")) {
  window.customElements.define("ll-bf26-price", BfPrice);
}
